import { Link } from "react-router-dom";
import { ScrollReveal } from "@/components/base/ScrollAnimations";
import { associationPartners, factoryPiicPartners, technologyPartners } from "@/mocks/homeData";

const tierBadgeStyles: Record<string, string> = {
  Platinum: "bg-slate-100 text-slate-700 border border-slate-300",
  Diamond: "bg-teal-50 text-teal-700 border border-teal-200",
  Gold: "bg-amber-50 text-amber-700 border border-amber-200",
};

const tierIcons: Record<string, string> = {
  Platinum: "ri-vip-diamond-fill",
  Diamond: "ri-vip-crown-fill",
  Gold: "ri-medal-fill",
};

function PartnerLogo({ name, logo }: { name: string; logo?: string }) {
  if (logo) {
    return (
      <img
        src={logo}
        alt={name}
        className="max-h-full max-w-full w-auto object-contain group-hover:scale-105 transition-transform duration-300"
      />
    );
  }
  return (
    <div className="w-12 h-12 md:w-14 md:h-14 rounded-lg bg-tie-navy/10 flex items-center justify-center group-hover:bg-tie-navy/20 transition-colors duration-300">
      <span className="text-sm md:text-base font-bold text-tie-navy">
        {name.split(" ").map((w) => w[0]).join("").substring(0, 2).toUpperCase()}
      </span>
    </div>
  );
}

export default function PartnersSection() {
  return (
    <section id="partners" className="bg-tie-grey-light py-16 md:py-24">
      <div className="w-full px-4 md:px-8 lg:px-12">
        {/* Header */}
        <ScrollReveal>
          <div className="text-center max-w-3xl mx-auto mb-10 md:mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-tie-teal/10 mb-4 md:mb-6">
              <span className="text-xs font-semibold text-tie-teal uppercase tracking-wider">
                / Our Partners
              </span>
            </div>
            <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl font-bold text-tie-text-primary">
              Strengthening Our Mission Together
            </h2>
            <p className="mt-3 md:mt-4 text-base md:text-lg text-tie-text-secondary">
              TIE is honoured to be backed by leading industry associations, factory partners, and technology solution providers who share our mission to build innovation capacity across Bangladesh's textile ecosystem.
            </p>
          </div>
        </ScrollReveal>

        {/* In Association Partners - logo cards */}
        <div className="grid md:grid-cols-3 gap-6 md:gap-8 max-w-7xl mx-auto mb-12 md:mb-16">
          {associationPartners.map((partner, index) => (
            <ScrollReveal key={partner.shortName} delay={index * 150}>
              <div className="bg-white rounded-2xl p-6 md:p-8 border border-tie-grey-dark hover:border-tie-teal/30 hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-tie-navy text-white text-xs font-semibold mb-5 md:mb-6">
                  <i className="ri-shield-star-line" />
                  In {partner.partnerType} Partner
                </div>

                <div className="h-20 md:h-24 flex items-center justify-start mb-5 md:mb-6">
                  <img
                    src={partner.logo}
                    alt={partner.name}
                    className="max-h-full max-w-[180px] md:max-w-[220px] w-auto object-contain"
                  />
                </div>

                <h3 className="text-lg md:text-xl font-bold text-tie-text-primary mb-2">
                  {partner.name}
                </h3>

                <div className="mt-4 md:mt-5 space-y-2">
                  {partner.focus.map((item) => (
                    <div
                      key={item}
                      className="flex items-center gap-2.5 text-sm text-tie-text-secondary"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-tie-teal shrink-0" />
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </ScrollReveal>
          ))}
        </div>

        {/* Factory PIIC Partners */}
        <ScrollReveal delay={200}>
          <div className="max-w-6xl mx-auto mb-12 md:mb-16">
            <div className="flex items-center justify-center gap-3 mb-6 md:mb-8">
              <div className="h-px w-16 bg-tie-grey-dark" />
              <p className="text-center text-xs font-semibold text-tie-text-muted uppercase tracking-wider">
                Factory PIIC Partners
              </p>
              <div className="h-px w-16 bg-tie-grey-dark" />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
              {factoryPiicPartners.map((partner) => (
                <div
                  key={partner.name}
                  className="group bg-white rounded-xl p-4 md:p-5 border border-tie-grey-dark hover:border-tie-teal/30 hover:shadow-lg hover:-translate-y-1.5 transition-all duration-300 flex flex-col items-center justify-center gap-3 cursor-pointer"
                >
                  <div className="h-14 md:h-16 flex items-center justify-center w-full">
                    <PartnerLogo name={partner.name} logo={partner.logo} />
                  </div>
                  <p className="text-xs md:text-sm font-semibold text-tie-text-primary text-center leading-snug">
                    {partner.name}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </ScrollReveal>

        {/* Technology Partners - tiered */}
        <ScrollReveal delay={300}>
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-center gap-3 mb-6 md:mb-8">
              <div className="h-px w-16 bg-tie-grey-dark" />
              <p className="text-center text-xs font-semibold text-tie-text-muted uppercase tracking-wider">
                Technology Partners
              </p>
              <div className="h-px w-16 bg-tie-grey-dark" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {technologyPartners.map((partner) => (
                <div
                  key={partner.name}
                  className="group bg-white rounded-xl p-5 md:p-6 border border-tie-grey-dark hover:border-tie-teal/30 hover:shadow-lg hover:-translate-y-1.5 transition-all duration-300 flex flex-col items-center justify-center gap-3 cursor-pointer"
                >
                  <div
                    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${tierBadgeStyles[partner.tier]}`}
                  >
                    <i className={tierIcons[partner.tier]} />
                    {partner.tier} Partner
                  </div>
                  <div className="h-14 md:h-16 flex items-center justify-center w-full">
                    <PartnerLogo name={partner.name} logo={partner.logo} />
                  </div>
                  <p className="text-sm md:text-base font-semibold text-tie-text-primary text-center leading-snug">
                    {partner.name}
                  </p>
                </div>
              ))}
            </div>

            <div className="text-center mt-8 md:mt-10">
              <Link
                to="/technology-partners"
                className="inline-flex items-center px-6 py-2.5 rounded-full bg-tie-navy text-white text-sm font-medium hover:bg-tie-navy-light transition-all duration-300 whitespace-nowrap"
              >
                View Technology Partnership Programme
                <i className="ri-arrow-right-line ml-2" />
              </Link>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}